import { useState, useRef } from "react";
import { toast } from "sonner";
import { ExternalLink, ImageDown, Check, Save, UploadCloud, Trash2, Pencil, Plus, Undo2 } from "lucide-react";
import { api, adminHeaders } from "../lib/api";
import { compressImageFile } from "../lib/imageCompress";
import { coverSrc } from "../lib/images";

const EDIT_FIELDS = ["note", "audience", "cover_url"];
const cellInput = "w-full bg-transparent border border-line rounded-sm px-3 py-2 text-vellum text-sm focus:border-crimson focus:outline-none placeholder:text-vellum-faint";
const barBtn = "inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2.5 border transition-colors";

// A book "needs a cover" when it has none or is still using a placeholder image.
const needsCover = (item) => {
  const c = (item.cover_url || "").trim();
  return !c || /placehold|placeholder|no[-_]?cover/i.test(c);
};

function buildPayload(item, draft) {
  const payload = { ...item, ...draft };
  delete payload.id;
  delete payload._id;
  return payload;
}

function BookRow({ item, draft, dirty, selected, onToggleSelect, onField, onSaveRow, onDeleteRow, onEditAll, storeLocal }) {
  const [busy, setBusy] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);
  const note = "note" in draft ? draft.note : (item.note || "");
  const audience = "audience" in draft ? draft.audience : (item.audience || "");
  const cover = "cover_url" in draft ? draft.cover_url : (item.cover_url || "");

  const save = async () => {
    setBusy(true);
    try { await onSaveRow(item); } finally { setBusy(false); }
  };

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("Please drop an image file"); return; }
    try {
      const dataUrl = await compressImageFile(file, { max: 500 });
      onField(item.id, "cover_url", dataUrl);
      toast.success("Cover added — remember to save");
    } catch { toast.error("Couldn't read that image"); }
  };

  const autoCover = async () => {
    setFetching(true);
    try {
      const r = await api.post(`/admin/library/${item.id}/fetch-cover`, { store_local: storeLocal }, { headers: adminHeaders() });
      if (r.data.found) { onField(item.id, "cover_url", r.data.cover_url); toast.success(`Cover found (${r.data.source})`); }
      else toast.error("No cover found online");
    } catch { toast.error("Cover lookup failed"); }
    finally { setFetching(false); }
  };

  return (
    <div className={`grid grid-cols-1 lg:grid-cols-[28px_56px_minmax(140px,1.1fr)_2fr_1.4fr_2fr_auto] gap-4 items-start px-4 py-5 transition-colors ${dirty ? "bg-crimson/5" : "hover:bg-ink-800/60"}`} data-testid={`book-row-${item.id}`}>
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onToggleSelect(item.id)}
        aria-label={`Select ${item.title}`}
        data-testid={`book-select-${item.id}`}
        className="w-4 h-4 mt-1 accent-crimson"
      />

      <div
        className={`relative w-14 h-20 shrink-0 bg-ink-700 overflow-hidden border cursor-pointer group ${dragging ? "border-crimson ring-2 ring-crimson/40" : "border-line"}`}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
        title="Drop or click to upload a cover"
        data-testid={`book-dropzone-${item.id}`}
      >
        {cover ? <img src={coverSrc(cover, "thumb")} loading="lazy" decoding="async" alt="" className="w-full h-full object-cover" /> : <span className="flex items-center justify-center w-full h-full text-[9px] text-vellum-muted">drop</span>}
        <span className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-ink/70"><UploadCloud className="w-4 h-4 text-vellum" /></span>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid={`book-file-${item.id}`} onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }} />
      </div>

      <div className="min-w-0">
        <p className="font-serif text-lg text-vellum leading-tight">{item.title}</p>
        <p className="font-mono text-[11px] text-vellum-muted mt-1">{item.creator}{item.year ? ` · ${item.year}` : ""}</p>
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-crimson mt-1">{item.media_type}{item.form ? ` · ${item.form}` : ""}{item.featured ? " · ★ pick" : ""}{dirty ? " · unsaved" : ""}</p>
        {(item.tags || []).length > 0 && (
          <p className="font-mono text-[10px] text-vellum-muted mt-1 truncate" title={(item.tags || []).join(", ")}>{(item.tags || []).join(", ")}</p>
        )}
        <p className="font-mono text-[10px] text-vellum-muted mt-1">{(item.links || []).length} link{(item.links || []).length === 1 ? "" : "s"}{item.field ? ` · ${item.field}` : ""}</p>
      </div>

      <div>
        <label className="lg:hidden font-mono text-[9px] uppercase tracking-[0.2em] text-vellum-muted block mb-1">Description</label>
        <textarea value={note} onChange={(e) => onField(item.id, "note", e.target.value)} rows={3} className={cellInput} data-testid={`book-note-${item.id}`} placeholder="Curatorial note…" />
      </div>

      <div>
        <label className="lg:hidden font-mono text-[9px] uppercase tracking-[0.2em] text-vellum-muted block mb-1">Why you should read this</label>
        <textarea value={audience} onChange={(e) => onField(item.id, "audience", e.target.value)} rows={3} className={cellInput} data-testid={`book-audience-${item.id}`} placeholder="Because it…" />
      </div>

      <div>
        <label className="lg:hidden font-mono text-[9px] uppercase tracking-[0.2em] text-vellum-muted block mb-1">Cover link</label>
        <input value={cover} onChange={(e) => onField(item.id, "cover_url", e.target.value)} className={cellInput} data-testid={`book-cover-${item.id}`} placeholder="Image URL…" />
        <div className="flex items-center gap-4 mt-2">
          <button type="button" onClick={autoCover} disabled={fetching} data-testid={`book-autocover-${item.id}`} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-crimson hover:text-vellum transition-colors disabled:opacity-40">
            <ImageDown className="w-3 h-3" /> {fetching ? "Finding…" : "Auto-fetch"}
          </button>
          {cover && !cover.startsWith("data:") && (
            <a href={cover} target="_blank" rel="noreferrer" data-testid={`book-coverlink-${item.id}`} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-vellum-muted hover:text-vellum transition-colors">
              <ExternalLink className="w-3 h-3" /> Open
            </a>
          )}
        </div>
      </div>

      <div className="flex items-start gap-3 lg:justify-end">
        <button onClick={save} disabled={busy || !dirty} data-testid={`book-save-${item.id}`} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2.5 bg-vellum text-ink hover:bg-crimson hover:text-onaccent transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
          <Check className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
        </button>
        <button
          onClick={() => onEditAll(item)}
          data-testid={`book-editall-${item.id}`}
          aria-label={`Edit all fields for ${item.title}`}
          title="Edit every field (title, author, year, field, form, tags, links, spotlight…)"
          className="p-2.5 border border-line text-vellum-muted hover:border-brass hover:text-brass transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onDeleteRow(item)}
          data-testid={`book-delete-${item.id}`}
          aria-label={`Delete ${item.title}`}
          title="Delete this book"
          className="p-2.5 border border-line text-vellum-muted hover:border-crimson hover:text-crimson transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function AdminBooksTable({
  library, onDone, onNew, onEditAll, onBulkDelete, onDeleteAll, deleting,
  storeLocal = true, onStoreLocalChange, onFetchAllCovers, coverBusy, coverBatch, onUndoCovers,
}) {
  const [q, setQ] = useState("");
  const [drafts, setDrafts] = useState({}); // { [id]: { note?, audience?, cover_url? } }
  const [savingAll, setSavingAll] = useState(false);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const books = (library || []).filter((i) => i.media_type === "book" || i.media_type === "essay");
  const byId = Object.fromEntries(books.map((b) => [b.id, b]));
  const missingCount = books.filter(needsCover).length;

  const rowDirty = (item) => {
    const d = drafts[item.id];
    if (!d) return false;
    return EDIT_FIELDS.some((f) => f in d && (d[f] ?? "") !== (item[f] ?? ""));
  };
  const dirtyIds = books.filter(rowDirty).map((b) => b.id);

  const onField = (id, field, value) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const clearDrafts = (ids) =>
    setDrafts((prev) => { const next = { ...prev }; ids.forEach((id) => delete next[id]); return next; });

  const toggleSelect = (id) =>
    setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  const saveRow = async (item) => {
    try {
      await api.put(`/library/${item.id}`, buildPayload(item, drafts[item.id] || {}), { headers: adminHeaders() });
      toast.success(`Saved “${item.title}”`);
      clearDrafts([item.id]);
      onDone?.();
    } catch { toast.error("Save failed"); }
  };

  const deleteRow = async (item) => {
    if (!window.confirm(`Delete “${item.title}”? This also removes its stored cover.`)) return;
    try {
      await api.delete(`/library/${item.id}`, { headers: adminHeaders() });
      toast.success(`Deleted “${item.title}”`);
      setSelected((prev) => { const next = new Set(prev); next.delete(item.id); return next; });
      onDone?.();
    } catch {
      toast.error("Could not delete");
    }
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected entr${ids.length === 1 ? "y" : "ies"}? This also removes their stored covers.`)) return;
    await onBulkDelete?.(ids);
    setSelected(new Set());
  };

  const saveAll = async () => {
    if (dirtyIds.length === 0) return;
    setSavingAll(true);
    try {
      const results = await Promise.allSettled(
        dirtyIds.map((id) => api.put(`/library/${id}`, buildPayload(byId[id], drafts[id] || {}), { headers: adminHeaders() }))
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - ok;
      if (ok) toast.success(`Saved ${ok} book${ok === 1 ? "" : "s"}${failed ? ` · ${failed} failed` : ""}`);
      if (failed && !ok) toast.error("Save all failed");
      const savedIds = dirtyIds.filter((_, i) => results[i].status === "fulfilled");
      clearDrafts(savedIds);
      onDone?.();
    } finally { setSavingAll(false); }
  };

  // Auto-fetch covers for every book that is missing one, in chunks (avoids gateway timeouts on large sets).
  const fetchMissing = async () => {
    const ids = books.filter(needsCover).map((b) => b.id);
    if (ids.length === 0) { toast.info("No books need a cover"); return; }
    if (!window.confirm(`Auto-fetch and save covers for the ${ids.length} book${ids.length === 1 ? "" : "s"} missing one? They'll be saved into the site.`)) return;
    setBulkBusy(true);
    const tid = toast.loading(`Fetching covers for ${ids.length} books…`);
    const chunks = [];
    for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30));
    let updated = 0, notFound = 0;
    try {
      for (let i = 0; i < chunks.length; i++) {
        const r = await api.post("/admin/library/fetch-covers", { ids: chunks[i], overwrite: true, store_local: storeLocal }, { headers: adminHeaders() });
        updated += r.data.updated || 0;
        notFound += r.data.not_found || 0;
        toast.loading(`Fetching covers… ${updated} found (batch ${i + 1}/${chunks.length})`, { id: tid });
      }
      toast.success(`${updated} cover${updated === 1 ? "" : "s"} added · ${notFound} not found`, { id: tid });
      onDone?.();
    } catch {
      toast.error("Cover lookup failed", { id: tid });
    } finally { setBulkBusy(false); }
  };

  const query = q.trim().toLowerCase();
  let rows = query ? books.filter((b) => `${b.title} ${b.creator}`.toLowerCase().includes(query)) : books;
  if (onlyMissing) rows = rows.filter(needsCover);
  const allVisibleSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

  return (
    <div data-testid="admin-books-table">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <p className="text-sm text-vellum-muted max-w-xl">Every book &amp; essay in one place — edit the description, why you should read it, or the cover inline, or open the full editor for title, author, year, field, form, themes, links and spotlight. Drop or click a cover thumbnail to upload your own image.</p>
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={onNew} data-testid="admin-add-library" className={`${barBtn} border-line text-vellum hover:border-crimson hover:text-crimson`}>
            <Plus className="w-3.5 h-3.5" /> New entry
          </button>
          <button onClick={() => setOnlyMissing((v) => !v)} data-testid="books-filter-missing" className={`${barBtn} rounded-sm ${onlyMissing ? "border-crimson text-crimson bg-crimson/10" : "border-line text-vellum-muted hover:text-vellum hover:border-vellum-muted"}`}>
            {onlyMissing ? "Showing: needs cover" : "Needs cover"}{missingCount ? ` (${missingCount})` : ""}
          </button>
          {missingCount > 0 && (
            <button onClick={fetchMissing} disabled={bulkBusy} data-testid="books-fetch-missing" className={`${barBtn} border-line text-vellum hover:border-crimson hover:text-crimson disabled:opacity-40 disabled:cursor-not-allowed`}>
              <ImageDown className="w-3.5 h-3.5" /> {bulkBusy ? "Fetching…" : `Auto-fetch missing (${missingCount})`}
            </button>
          )}
          <button onClick={onFetchAllCovers} disabled={coverBusy} data-testid="admin-fetch-covers" className={`${barBtn} border-line text-vellum hover:border-crimson hover:text-crimson disabled:opacity-40 disabled:cursor-not-allowed`}>
            <ImageDown className="w-3.5 h-3.5" /> {coverBusy ? "Finding covers…" : "Auto-fetch all covers"}
          </button>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or author…" data-testid="books-search" className="w-52 bg-transparent border border-line rounded-sm px-4 py-2.5 text-vellum text-sm focus:border-crimson focus:outline-none placeholder:text-vellum-muted" />
          <button onClick={saveAll} disabled={savingAll || dirtyIds.length === 0} data-testid="books-save-all" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-2.5 bg-crimson text-onaccent hover:bg-vellum hover:text-ink transition-colors disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap">
            <Save className="w-4 h-4" /> {savingAll ? "Saving…" : `Save all changes${dirtyIds.length ? ` (${dirtyIds.length})` : ""}`}
          </button>
        </div>
      </div>

      {coverBatch && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-4 border border-crimson/40 bg-crimson/5 p-4" data-testid="cover-undo-bar">
          <p className="text-sm text-vellum">Book covers updated — <span className="text-vellum-muted">{coverBatch.label}</span>. Not happy with the result?</p>
          <button onClick={onUndoCovers} disabled={coverBusy} data-testid="cover-undo" className={`${barBtn} border-crimson text-crimson hover:bg-crimson hover:text-onaccent disabled:opacity-40`}>
            <Undo2 className="w-3.5 h-3.5" /> {coverBusy ? "Undoing…" : "Undo cover update"}
          </button>
        </div>
      )}

      {/* Selection + destructive actions */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-4">
        <div className="flex flex-wrap items-center gap-6">
          <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="books-select-all"
              checked={allVisibleSelected}
              onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())}
              className="w-4 h-4 accent-crimson"
            />
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-vellum-muted">
              {selected.size > 0 ? `${selected.size} selected` : `Select all shown (${rows.length})`}
            </span>
          </label>
          <label className="inline-flex items-center gap-2.5 cursor-pointer select-none" data-testid="cover-store-local">
            <input type="checkbox" checked={storeLocal} onChange={(e) => onStoreLocalChange?.(e.target.checked)} className="w-4 h-4 accent-crimson" />
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-vellum-muted">Save into site <span className="text-vellum-muted normal-case tracking-normal">(won&apos;t break if source changes)</span></span>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={deleteSelected}
            disabled={selected.size === 0 || deleting}
            data-testid="books-delete-selected"
            className={`${barBtn} border-line text-vellum-muted hover:border-crimson hover:text-crimson disabled:opacity-30`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete selected{selected.size ? ` (${selected.size})` : ""}
          </button>
          <button
            onClick={onDeleteAll}
            disabled={books.length === 0 || deleting}
            data-testid="books-delete-all"
            className={`${barBtn} border-crimson/60 text-crimson hover:bg-crimson hover:text-onaccent disabled:opacity-30`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete all ({books.length})
          </button>
        </div>
      </div>

      <div className="hidden lg:grid grid-cols-[28px_56px_minmax(140px,1.1fr)_2fr_1.4fr_2fr_auto] gap-4 px-4 pb-3 border-b border-line font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-muted">
        <span /><span>Cover</span><span>Book</span><span>Description</span><span>Why you should read this</span><span>Cover link</span><span className="text-right">Actions</span>
      </div>

      <div className="divide-y divide-line border-b border-line">
        {rows.map((b) => (
          <BookRow
            key={b.id}
            item={b}
            draft={drafts[b.id] || {}}
            dirty={rowDirty(b)}
            selected={selected.has(b.id)}
            onToggleSelect={toggleSelect}
            onField={onField}
            onSaveRow={saveRow}
            onDeleteRow={deleteRow}
            onEditAll={onEditAll}
            storeLocal={storeLocal}
          />
        ))}
        {rows.length === 0 && <p className="px-4 py-10 text-center text-vellum-muted text-sm">No books match your search.</p>}
      </div>
      <p className="mt-4 font-mono text-xs text-vellum-muted">{rows.length} book{rows.length === 1 ? "" : "s"}{dirtyIds.length ? ` · ${dirtyIds.length} unsaved` : ""}</p>
    </div>
  );
}
